import { Tab, Tabs } from '@mui/material';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { useInfluencer } from '@client/new/common/LiteUser.mapper';
import { AsyncRenderer } from '../../components/AsyncRenderer/AsyncRenderer';
import { Analytics } from './Analytics';
import { ConfigurableSourceType, Integrations } from './integrations';
import { usePersistedTabHash } from '../../hooks/useTabHash';
import { keys } from '../../helpers/general';
import { PropsWithChildren, useState } from 'react';
import { useURLBuilderState } from './campaign-setup/useURLBuilderState';
import { TitledPage } from '@client/new/components/Misc';
import { CampaignSetup } from './campaign-setup';
import { getRole } from '../../components/AdminMenu/helpers';
import { AutomationsView } from './automations/view/Automations.view';

const TagLabel = (
  p: PropsWithChildren<{
    variant: 'primary' | 'secondary';
    small?: boolean;
  }>,
) => (
  <span
    style={{
      background: p.variant === 'primary' ? '#f97317' : '#0ACC2F',
      borderRadius: 4,
      fontSize: '10px',
      fontWeight: 500,
      alignSelf: 'flex-start',
      color: 'rgba(255, 255,255,.9)',
      padding: '4px',
      position: 'absolute',
      textTransform: 'uppercase',
      marginLeft: '16px',
      height: '18px',
      lineHeight: '10px',
    }}
  >
    {p.children}
  </span>
);

export const tabs = {
  'campaign-setup': 'Campaign setup',
  automations: 'Automations',
  integrations: 'Integrations',
  performance: 'Performance',
};

export type MarketingTab = keyof typeof tabs;

const marketingPageTabKey = 'Marketing.Tab';

export const MarketingPage = () => {
  const urlBuilderState = useURLBuilderState();
  const influencerId = useInfluencerId();

  const [sourceType, setSourceType] = useState<
    ConfigurableSourceType | undefined
  >(undefined);

  const creator = useInfluencer(influencerId);
  const role = getRole();
  // GRW-733: creators see Automations only once an admin sets the flag on
  // their account. Admins and moderators see it for everyone.
  const automationsEnabledForCreator =
    creator.type === 'fetched' &&
    creator.value.customizations?.isAutomationsEnabled === true;
  const visibleTabs =
    role === 'admin' || role === 'moderator' || automationsEnabledForCreator
      ? tabs
      : (Object.fromEntries(
          Object.entries(tabs).filter(([key]) => key !== 'automations'),
        ) as Omit<typeof tabs, 'automations'>);
  const { onTabChange, tabHash, setTabHash } =
    usePersistedTabHash<MarketingTab>(
      keys(visibleTabs),
      'campaign-setup',
      marketingPageTabKey,
    );
  const title = (
    <span>
      Marketing <TagLabel variant="primary">beta</TagLabel>
    </span>
  );
  return (
    <TitledPage title={title}>
      <Tabs value={tabHash} onChange={onTabChange}>
        {keys(visibleTabs).map((hash) => (
          <Tab
            key={hash}
            value={hash}
            label={visibleTabs[hash]}
            iconPosition="start"
            style={{
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'Visuelt',
              lineHeight: '20px',
              color: 'black',
            }}
          />
        ))}
      </Tabs>
      <div
        className="mt-5"
        style={{
          maxWidth: tabHash === 'performance' ? '1500px' : '960px',
        }}
      >
        <AsyncRenderer
          data={creator}
          renderer={(creator) => {
            switch (tabHash) {
              case 'performance':
                return <Analytics creator={creator} />;

              case 'automations':
                return (
                  <AutomationsView
                    key={influencerId}
                    creatorId={influencerId}
                  />
                );

              case 'campaign-setup':
                return (
                  <CampaignSetup
                    changeTab={setTabHash}
                    creator={creator}
                    {...urlBuilderState}
                    onNavigateToIntegration={(type) => {
                      setSourceType(type);
                      onTabChange(null, 'integrations');
                    }}
                  />
                );

              case 'integrations':
                return (
                  <Integrations creator={creator} sourceType={sourceType} />
                );
            }
          }}
        />
      </div>
    </TitledPage>
  );
};
